import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Building2 } from "lucide-react";
import ProfileGate from "@/components/ProfileGate";

const inr = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;


// The contractor supplies these. Name, client, position and the monthly
// amount are the admin's and are deliberately not editable here.
import { toast } from "sonner";
import { PROFILE_FIELDS, PROFILE_REQUIRED } from "@/components/ProfileGate";

// One list, not three. The gate, this card and the backend must ask for
// exactly the same things. When they drifted apart a contractor filled in
// everything he could see, saved successfully, and the gate reappeared as
// though nothing had happened - the two emergency contact fields were only
// ever on the gate, never on this card.
const FIELDS = [...PROFILE_FIELDS, ["upi_id", "UPI id (optional)"]];
const REQUIRED = PROFILE_REQUIRED;

export default function StaffingPortalPage() {
  const [me, setMe] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState("");
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [a, b] = await Promise.all([
          api.get("/staffing/me"),
          api.get("/staffing/me/statements"),
        ]);
        setMe(a.data);
        setForm(a.data || {});
        setRows(b.data || []);
      } catch (e) {
        window.location.href = "/staffing/login";
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return <div className="min-h-screen grid place-items-center text-sm text-zinc-500">Loading...</div>;
  }

  const totalPaid = rows.reduce((t, r) => t + Number(r.net || 0), 0);
  const complete = REQUIRED.every((f) => String(form[f] || "").trim());
  const showForm = editing || !complete;

  const downloadAllPayslips = async () => {
    try {
      const res = await api.get("/staffing/me/payslips.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "payslips.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadPayslip = async (r) => {
    try {
      const res = await api.get(`/staffing/me/statements/${r.id}/pdf`,
                                { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `payslip-${r.month}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const cancelEdit = () => {
    const f = {};
    FIELDS.forEach(([k]) => { f[k] = (me || {})[k] || ""; });
    setForm(f);
    setSaveErr("");
    setEditing(false);
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaveErr("");
    setSaving(true);
    try {
      const { data } = await api.patch("/staffing/me", form);
      const fresh = data || form;
      setForm(fresh);
      setMe({ ...me, ...(data || {}) });
      // Say something. A silent success looks exactly like a dead button,
      // and if anything is still outstanding the person needs to be told
      // which box, not left to guess why the prompt keeps coming back.
      const still = REQUIRED.filter((f) => !String(fresh[f] || "").trim());
      if (still.length) {
        const labels = still.map((f) => (FIELDS.find(([k]) => k === f) || [f, f])[1]);
        const msg = "Saved. Still needed: " + labels.join(", ");
        setSaveErr(msg);
        toast.error(msg);
      } else {
        toast.success("Details saved");
        setEditing(false);
      }
    } catch (err) {
      setSaveErr(formatApiError(err));
      toast.error(formatApiError(err));
    }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="border-b border-zinc-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-emerald-600 grid place-items-center">
              <Building2 className="h-4 w-4 text-white" strokeWidth={1.75} />
            </div>
            <div>
              <div className="text-sm font-semibold">{me?.name}</div>
              <div className="text-xs text-zinc-500">
                {me?.position}{me?.client ? ` · ${me.client}` : ""}
              </div>
            </div>
          </div>
          <button className="text-sm text-zinc-600 hover:underline"
                  onClick={async () => {
                    try { await api.post("/auth/logout"); } catch (e) {}
                    window.location.href = "/staffing/login";
                  }}>Sign out</button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <Card className="border-zinc-200 shadow-none">
            <CardContent className="pt-5">
              <div className="text-xs text-zinc-500">Monthly amount</div>
              <div className="text-2xl font-semibold tabular-nums">{inr(me?.monthly_amount)}</div>
            </CardContent>
          </Card>
          <Card className="border-zinc-200 shadow-none">
            <CardContent className="pt-5">
              <div className="text-xs text-zinc-500">Paid to date</div>
              <div className="text-2xl font-semibold tabular-nums">{inr(totalPaid)}</div>
            </CardContent>
          </Card>
        </div>

        <Card className="border-zinc-200 shadow-none">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-base font-semibold">Your details</CardTitle>
              {!showForm && (
                <Button type="button" variant="outline" size="sm"
                        onClick={() => setEditing(true)} data-testid="staff-edit-profile">
                  Edit
                </Button>
              )}
            </div>
            {!complete ? (
              <p className="text-sm text-amber-700">
                We need your bank details before we can pay you.
              </p>
            ) : null}
          </CardHeader>
          <CardContent>
            {showForm ? (
              <form onSubmit={saveProfile} className="grid grid-cols-2 gap-3">
                {FIELDS.map(([k, label]) => (
                  <div key={k}>
                    <label className="text-xs text-zinc-500">{label}</label>
                    <input className="w-full border border-zinc-300 rounded px-2 py-1.5 text-sm"
                           value={form[k] || ""}
                           onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                  </div>
                ))}
                <div className="col-span-2 flex items-center justify-between pt-1">
                  {saveErr ? <span className="text-sm text-red-600">{saveErr}</span> : <span />}
                  <div className="flex items-center gap-2">
                    {complete && (
                      <Button type="button" variant="outline" onClick={cancelEdit}
                              data-testid="staff-cancel-profile">Cancel</Button>
                    )}
                    <Button type="submit" disabled={saving} data-testid="staff-save-profile">
                      {saving ? "Saving\u2026" : "Save details"}
                    </Button>
                  </div>
                </div>
              </form>
            ) : (
              <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
                {FIELDS.map(([k, label]) => (
                  <div key={k}>
                    <dt className="text-xs text-zinc-500">{label}</dt>
                    <dd className="text-sm text-zinc-900 break-words">{form[k] || "\u2014"}</dd>
                  </div>
                ))}
              </dl>
            )}
          </CardContent>
        </Card>

        <Card className="border-zinc-200 shadow-none">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-base font-semibold">Your monthly statements</CardTitle>
              {rows.length > 0 && (
                <Button size="sm" variant="outline" onClick={downloadAllPayslips}
                        data-testid="staff-payslips-all">Download all</Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">TDS</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                      <TableHead className="text-right">Payslip</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id || r.month}>
                    <TableCell className="font-medium">
                      {r.period_label}
                      {r.days_worked != null && r.days_worked !== r.days_in_month ? (
                        <span className="text-xs text-zinc-500"> ({r.days_worked} of {r.days_in_month} days)</span>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{inr(r.gross)}</TableCell>
                    <TableCell className="text-right tabular-nums text-zinc-500">
                      {Number(r.tds || 0) ? inr(r.tds) : "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold text-emerald-700">{inr(r.net)}</TableCell>
                    <TableCell className="text-right">
                      <button type="button" onClick={() => downloadPayslip(r)}
                              data-testid={`payslip-${r.month}`}
                              className="text-xs text-emerald-700 hover:underline">
                        Download
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
                {!rows.length ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-sm text-zinc-500 py-10">
                    Nothing here yet.
                  </TableCell></TableRow>
                ) : null}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <PortalDocuments />

          <p className="text-xs text-zinc-500">
          Questions about a payment? Email support@arborvitae.in.
        </p>
      </div>
    </div>
  );
}

// ------------------------------------------------------- document folder
//
// Read-only on this side. Uploading is the admin's job, so the contractor
// gets a list and a download and nothing that can change it.

const PORTAL_DOC_LABELS = {
  agreement: "Agreement",
  offer_letter: "Offer letter",
  invoice: "Invoice",
  id_proof: "ID proof",
  bank_proof: "Bank proof",
  statement: "Statement",
  other: "Other",
};

function PortalDocuments() {
  const [docs, setDocs] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let live = true;
    api.get("/staffing/me/documents")
      .then(({ data }) => { if (live) setDocs(data || []); })
      // An empty or failed folder should not throw a red toast at someone
      // who only came here for their payslip.
      .catch(() => {})
      .finally(() => { if (live) setLoaded(true); });
    return () => { live = false; };
  }, []);

  if (!loaded) return null;
  return (
    <Card className="border-zinc-200 shadow-none">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold">Your documents</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {docs.length === 0 ? (
          <p className="text-sm text-zinc-500 px-6 pb-6">
            Nothing here yet. Your agreement and offer letter will appear here
            once Arborvitae uploads them.
          </p>
        ) : (
          <>
            <Table>
              <TableHeader><TableRow>
                <TableHead>File</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">&nbsp;</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {docs.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell className="font-medium">
                      {d.filename}
                      {d.note ? <span className="block text-[11px] text-zinc-400">{d.note}</span> : null}
                    </TableCell>
                    <TableCell className="text-sm text-zinc-600">
                      {PORTAL_DOC_LABELS[d.category] || d.category}
                    </TableCell>
                    <TableCell className="text-right">
                      <button className="text-xs text-emerald-700 hover:underline"
                              onClick={() => window.open(`/api/staffing/me/documents/${d.id}/download`, "_blank")}>
                        Download
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="px-6 py-3 border-t border-zinc-100 text-right">
              <button className="text-xs text-emerald-700 hover:underline"
                      onClick={() => window.open("/api/staffing/me/documents/download-all", "_blank")}>
                Download all as zip
              </button>
              <ProfileGate me={me} endpoint="/staffing/me" onSaved={setMe} />
      </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
